import { useCallback, useRef, useState } from "react";
import { Palmtree, Clock, Timer, Bell, type LucideIcon } from "lucide-react";

import { cn } from "../../lib/utils.js";
import type { NotifCategory } from "./notification-model.js";

// Category tiles: a tint background plus the strong foreground from the SAME
// token family, so both themes and the contrast floor come for free. Leave uses
// the accent (purple) family, Attendance the info family, Hours the cyan family.
export const CATEGORY_ICON: Record<NotifCategory, LucideIcon> = {
  leave:      Palmtree,
  attendance: Clock,
  hours:      Timer,
  other:      Bell,
};

export const CATEGORY_TILE: Record<NotifCategory, string> = {
  leave:      "bg-[var(--color-accent-bg)] text-[var(--color-accent-fg)]",
  attendance: "bg-[var(--color-info-bg)] text-[var(--color-info-fg)]",
  hours:      "bg-[var(--color-cyan-bg)] text-[var(--color-cyan-fg)]",
  other:      "bg-surface-100 text-surface-600",
};

export const CATEGORY_LABEL: Record<NotifCategory, string> = {
  leave: "Leave", attendance: "Attendance", hours: "Hours", other: "Other",
};

export const FOCUS = "focus-visible:outline-2 focus-visible:outline-offset-[-2px] focus-visible:outline-brand-500";

/** The rounded category tile that opens every row. */
export function CategoryTile({ category, size = 32, className }: { category: NotifCategory; size?: 32 | 40; className?: string }) {
  const Icon = CATEGORY_ICON[category];
  return (
    <span
      aria-hidden="true"
      className={cn(
        "notif-tint flex shrink-0 items-center justify-center",
        size === 40 ? "h-10 w-10 rounded-xl" : "h-8 w-8 rounded-[10px]",
        CATEGORY_TILE[category],
        className,
      )}
    >
      <Icon size={size === 40 ? 18 : 15} />
    </span>
  );
}

/** The unread dot. Always rendered, so it can scale out rather than disappear. */
export function UnreadDot({ unread }: { unread: boolean }) {
  return (
    <span
      aria-hidden="true"
      className={cn(
        "notif-dot inline-block h-2 w-2 shrink-0 rounded-full bg-brand-600",
        unread ? "scale-100 opacity-100" : "scale-0 opacity-0",
      )}
    />
  );
}

const STATUS_TAG: Record<string, string> = {
  approved: "bg-[var(--color-success-bg)] text-[var(--color-success-fg)]",
  declined: "bg-[var(--color-error-bg)] text-[var(--color-error-fg)]",
};

/** "Approved" / "Declined" tag. Info-status rows show none. */
export function StatusTag({ status }: { status: string }) {
  if (status !== "approved" && status !== "declined") return null;
  return (
    <span className={cn("shrink-0 rounded-[7px] px-1.5 py-0.5 text-[11px] font-bold", STATUS_TAG[status])}>
      {status === "approved" ? "Approved" : "Declined"}
    </span>
  );
}

/**
 * Drives the staggered entrance.
 *
 * A re-used DOM node will not replay an animation whose name has not changed, so
 * each trigger alternates between two identically-defined keyframes. The class is
 * cleared after it finishes, so ordinary updates (marking one row read) do not
 * replay the whole list.
 */
export function useReplayAnimation(clearAfterMs = 900) {
  const [phase, setPhase] = useState<0 | 1 | null>(null);
  const flip = useRef<0 | 1>(0);
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);

  const replay = useCallback(() => {
    flip.current = flip.current === 0 ? 1 : 0;
    setPhase(flip.current);
    if (timer.current) clearTimeout(timer.current);
    timer.current = setTimeout(() => setPhase(null), clearAfterMs);
  }, [clearAfterMs]);

  return { phase, replay };
}

/** Per-row class for the staggered entrance; nothing once it has been cleared. */
export function rowInClass(phase: 0 | 1 | null): string {
  return phase === null ? "" : phase === 0 ? "notif-row-in-0" : "notif-row-in-1";
}

/** 45ms per row, capped at 9 so a long list does not crawl in. */
export function rowDelay(index: number, cap = 9): string {
  return `${Math.min(index, cap) * 45}ms`;
}

/** Empty state shared by both list surfaces. */
export function EmptyState({ compact = false }: { compact?: boolean }) {
  return (
    <div className={cn("flex flex-col items-center justify-center text-center", compact ? "py-6" : "py-10")}>
      <span className="mb-2 flex h-10 w-10 items-center justify-center rounded-full bg-[var(--color-success-bg)] text-[var(--color-success-fg)]">
        ✓
      </span>
      <p className="text-[13px] font-semibold text-surface-900">You're all caught up</p>
      {!compact && <p className="mt-0.5 text-[12px] text-surface-500">New notifications will show up here.</p>}
    </div>
  );
}

/**
 * Placeholder rows, shown while the first fetch is in flight. Without this the
 * list renders an empty array for a beat and flashes "You're all caught up"
 * before any data has arrived.
 */
export function NotificationSkeleton({ rows = 3 }: { rows?: number }) {
  return (
    <div className="flex flex-col gap-1" aria-hidden="true">
      {Array.from({ length: rows }, (_, i) => (
        <div key={i} className="skeleton flex items-start gap-2.5 rounded-xl p-2 pl-3" style={{ animationDelay: `${i * 120}ms` }}>
          <span className="h-8 w-8 shrink-0 rounded-[10px] bg-surface-200" />
          <span className="min-w-0 flex-1">
            <span className="flex items-center gap-2">
              <span className="h-3 flex-1 rounded bg-surface-200" />
              <span className="h-2.5 w-8 shrink-0 rounded bg-surface-100" />
            </span>
            <span className="mt-1.5 block h-2.5 w-4/5 rounded bg-surface-100" />
          </span>
        </div>
      ))}
    </div>
  );
}
